import type { components } from "../../shared/api/generated/trading-engine.js";
import type { AnalysisReport } from "./queries.js";

type Category = components["schemas"]["AiAnalysisContextCategory"];
const labels: Record<Category, string> = {
  company_news: "Company news",
  industry: "Industry & sector",
  fundamentals: "Fundamentals",
  earnings: "Earnings",
  monetary_policy: "Fed rates & meetings",
  macro: "Inflation & employment",
  market_risk: "Broad-market risk",
};
function safeLink(value: string): string | undefined {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.username === "" && url.password === ""
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}
function date(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    timeZone: "America/New_York",
    timeZoneName: "short",
  });
}

export function AnalysisContext({ report }: { readonly report: AnalysisReport }) {
  const context = report.snapshot?.context;
  if (context === undefined)
    return (
      <p className="field-hint">
        This older report did not collect company news, fundamentals or macro context.
      </p>
    );
  const sourceById = new Map(context.sources.map((source) => [source.id, source]));
  return (
    <section className="section-stack" aria-label="Research context">
      <h3>Company, industry & economic context</h3>
      <p className="field-hint">
        Saved sources and their publication dates are shown below. Coverage is bounded; missing
        evidence is an uncertainty, not a neutral signal.
      </p>
      <div className="analysis-context-grid">
        {context.coverage.map((coverage) => {
          const factor = report.decision?.contextFactors?.find(
            (item) => item.category === coverage.category,
          );
          const sourceCount = context.sources.filter(
            (source) => source.category === coverage.category,
          ).length;
          return (
            <section
              className="analysis-context-card"
              key={coverage.category}
              aria-label={labels[coverage.category]}
            >
              <h4>{labels[coverage.category]}</h4>
              <div className="button-row">
                <span className="status-pill">
                  {coverage.status === "available"
                    ? "Evidence available"
                    : coverage.status === "partial"
                      ? "Partial coverage"
                      : "Unavailable"}
                </span>
                <span className="status-pill" data-tone={factor?.tone ?? "unknown"}>
                  {factor?.tone === undefined || factor.tone === "unknown"
                    ? "Uncertain"
                    : factor.tone[0]?.toUpperCase() + factor.tone.slice(1)}
                </span>
              </div>
              <p>{factor?.summary ?? "No factor assessment was saved."}</p>
              <p className="field-hint">{coverage.detail}</p>
              <small>
                {sourceCount} saved source{sourceCount === 1 ? "" : "s"}
              </small>
              {factor?.evidenceIds.length ? (
                <ul className="analysis-context-links">
                  {factor.evidenceIds.map((id) => {
                    const source = sourceById.get(id);
                    const href = source === undefined ? undefined : safeLink(source.url);
                    return (
                      <li key={id}>
                        {href === undefined ? (
                          <span>{id}</span>
                        ) : (
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            {source?.title}
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </section>
          );
        })}
      </div>
      <details className="analysis-context-sources">
        <summary>Saved research sources ({context.sources.length})</summary>
        {context.sources.map((source) => {
          const href = safeLink(source.url);
          return (
            <article key={source.id} className="analysis-context-source">
              <h4>
                {href === undefined ? (
                  source.title
                ) : (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    {source.title}
                  </a>
                )}
              </h4>
              <p>{source.summary}</p>
              <p className="field-hint">
                {labels[source.category]} · {source.sourceName}
                {source.publishedAt === undefined
                  ? " · Publication time not supplied"
                  : ` · Published: ${date(source.publishedAt)}`}
                {source.updatedAt === undefined ? "" : ` · Updated: ${date(source.updatedAt)}`}
                {source.eventDate === undefined
                  ? ""
                  : ` · Scheduled event: ${source.eventDate} (outcome unknown)`}{" "}
                · Retrieved: {date(source.retrievedAt)}
              </p>
              {source.values === undefined ? null : (
                <dl>
                  {Object.entries(source.values).map(([key, value]) => (
                    <div key={key}>
                      <dt>{key}</dt>
                      <dd>{value.toLocaleString(undefined, { maximumFractionDigits: 4 })}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <small>Evidence ID: {source.id}</small>
            </article>
          );
        })}
      </details>
    </section>
  );
}
